import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaSignInAlt, FaPause, FaPlay, FaChartLine, FaLink, FaWallet, FaSpinner, FaUser, FaArrowLeft, FaTimes } from 'react-icons/fa';
import './EditClient.css';

// API endpoints
const SUSPEND_UNSUSPEND_URL = 'https://server.tradefxonline.com/admin_suspend_unsuspend_client.php';
const LOGIN_AS_CLIENT_URL = 'https://server.tradefxonline.com/admin_login_client.php';

const EditClient = () => {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  
  // Determine if this is view mode or edit mode based on the current path
  const isViewMode = window.location.pathname.includes('/view-client/');
  const [clientData, setClientData] = useState({
    id: '',
    first_name: '',
    last_name: '',
    dob: '',
    email: '',
    phone: '',
    zipcode: '',
    country: '',
    state: '',
    city: '',
    password: '',
    suspend: 0, // Add suspend status
    // Display fields for UI
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    zipCode: ''
  });

  const [tradingAccounts, setTradingAccounts] = useState([
    {
      id: 67543,
      leverage: 100,
      accountType: 'Demo',
      status: 'verified',
      balance: 0.00,
      createdAt: 'Wednesday, April 09, 2025 11:36:39 PM',
      lastSynced: '3 months ago'
    }
  ]);

  const [showTradingAccountModal, setShowTradingAccountModal] = useState(false);
  const [showTradingAccountsView, setShowTradingAccountsView] = useState(false);
  const [agentAccount, setAgentAccount] = useState('67567');
  const [isLoading, setIsLoading] = useState(false);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    // Load client data based on clientId
    if (clientId) {
      loadClientData(clientId);
    }
  }, [clientId]);

  // Helper function to split full name
  const splitName = (fullName) => {
    if (!fullName) return { first: '', last: '' };
    const parts = fullName.trim().split(' ');
    const first = parts[0] || '';
    const last = parts.slice(1).join(' ') || '';
    return { first, last };
  };

  const loadClientData = async (id) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`https://server.tradefxonline.com/admin_viewusers.php?id=${id}`);
      
      if (response.data.success && response.data.data) {
        const userData = response.data.data;
        
        // Set both API format and display format
        const transformedData = {
          // API format fields
          id: userData.id,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          dob: userData.dob || '',
          email: userData.email || '',
          phone: userData.phone || '',
          zipcode: userData.zipcode || '',
          country: userData.country || '',
          state: userData.state || '',
          city: userData.city || '',
          password: userData.password || '',
          suspend: userData.suspend || 0, // Add suspend status from API
          // Display format fields for UI
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          dateOfBirth: userData.dob || '',
          phoneNumber: userData.phone || '',
          zipCode: userData.zipcode || ''
        };
        
        setClientData(transformedData);
        
        // Load trading accounts if email is available
        if (userData.email) {
          loadTradingAccounts(userData.email);
        }
      } else {
        console.error('Failed to load user data:', response.data.message);
        alert('Failed to load user data');
      }
    } catch (error) {
      console.error('Error loading client data:', error);
      alert('Error loading client data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadTradingAccounts = async (email) => {
    try {
      const response = await axios.get(`https://server.tradefxonline.com/admin_getliveaccounts.php?email=${email}`);
      
      if (response.data.status === 'success' && response.data.data) {
        setTradingAccounts(response.data.data);
      }
    } catch (error) {
      console.error('Error loading trading accounts:', error);
    }
   };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Sync display fields with API fields
      if (name === 'firstName') updated.first_name = value;
      if (name === 'lastName') updated.last_name = value;
      if (name === 'dateOfBirth') updated.dob = value;
      if (name === 'phoneNumber') updated.phone = value;
      if (name === 'zipCode') updated.zipcode = value;
      
      return updated;
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Prepare data for API - only send fields that are in the form
      const updateData = {
        id: clientData.id,
        first_name: clientData.first_name,
        last_name: clientData.last_name,
        country: clientData.country,
        phone: clientData.phone,
        dob: clientData.dob,
        email: clientData.email,
        password: clientData.password
      };
      
      // Add optional fields only if they have values
      if (clientData.city) updateData.city = clientData.city;
      if (clientData.state) updateData.state = clientData.state;
      if (clientData.zipcode) updateData.zipcode = clientData.zipcode;
      
      const response = await axios.post('https://server.tradefxonline.com/admin_editusers.php', updateData);
      
      if (response.data.success) {
        alert('Client data updated successfully!');
        // Reload the data to reflect changes
        loadClientData(clientId);
      } else {
        alert('Failed to update client data: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving client data:', error);
      alert('Error saving client data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      verified: 'status-verified',
      pending: 'status-pending',
      rejected: 'status-rejected'
    };
    
    return (
      <span className={`status-badge ${statusClasses[status] || 'status-pending'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    alert(`New password generated: ${newPassword}`);
  };

  // Handle suspend/unsuspend client
  const handleSuspendUnsuspend = async () => {
    setSuspendLoading(true);
    try {
      const action = clientData.suspend === 1 ? 'unsuspend' : 'suspend';
      const response = await axios.post(SUSPEND_UNSUSPEND_URL, {
        admin_email: 'qfxmarketsadmin@gmail.com',
        client_email: clientData.email,
        action: action
      });

      if (response.data.success) {
        // Update local state
        setClientData(prev => ({
          ...prev,
          suspend: action === 'suspend' ? 1 : 0
        }));
        
        alert(response.data.message || `Client has been ${action}ed successfully`);
      } else {
        alert('Failed to ' + action + ' client: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error suspending/unsuspending client:', error);
      alert('Error processing request. Please try again.');
    } finally {
      setSuspendLoading(false);
    }
  };

  // Handle login as client
const handleLoginAsClient = async () => {
  setLoginLoading(true);
  try {
    // Retrieve adminData from localStorage
    const adminData = JSON.parse(localStorage.getItem('adminData') || 'null');

    if (!adminData || !adminData.email) {
      alert('Admin session not found. Please log in again.');
      setLoginLoading(false);
      return;
    }

    const adminEmail = adminData.email;

    const response = await axios.post(LOGIN_AS_CLIENT_URL, {
      admin_email: adminEmail,
      client_email: clientData.email
    });

    if (response.data.success) {
      const loginUrl = `https://user.tradefxonline.com/login-admin?client=${encodeURIComponent(clientData.email)}&admin=${encodeURIComponent(adminEmail)}`;
      // const loginUrl = `http://localhost:8080/login-admin?client=${encodeURIComponent(clientData.email)}&admin=${encodeURIComponent(adminEmail)}`;
      window.open(loginUrl, '_blank');
    } else {
      alert('Failed to login as client: ' + (response.data.message || 'Unknown error'));
    }
  } catch (error) {
    console.error('Error logging in as client:', error);
    alert('Error processing login request. Please try again.');
  } finally {
    setLoginLoading(false);
  }
};

  return (
    <div className="edit-client-page">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading client data...</p>
        </div>
      )}
      
      <div className="page-header">
        <h2>{isViewMode ? 'View Client Information' : 'Edit Client Information'}</h2>
      
      </div>
      
      <div className="edit-client-container">
      <div className="edit-client-layout">
        {/* Left Sidebar */}
        <div className="edit-client-sidebar">
          <div className="client-profile">
            <div className="profile-avatar">
              <div className="avatar-placeholder">
                <FaUser />
              </div>
            </div>
            <h2 className="client-name">{clientData.firstName} {clientData.lastName}</h2>
          </div>

          <div className="client-stats">
            <div className="stat-item">
              <span className="stat-label">Email</span>
              <span className="stat-value email-badge">{clientData.email}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Country</span>
              <span className="stat-value country-badge">{clientData.country}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Trading Accounts</span>
              <span className="stat-value accounts-badge">{tradingAccounts?.length || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Phone</span>
              <span className="stat-value phone-badge">{clientData.phone}</span>
            </div>
          </div>

          <div className="action-buttons">
            <button 
              className="action-btn login-btn"
              onClick={handleLoginAsClient}
              disabled={loginLoading || !clientData.email}
            >
              {loginLoading ? (
                <>
                  <FaSpinner className="fa-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <FaSignInAlt />
                  Login as {clientData.firstName || 'Client'}
                </>
              )}
            </button>
            <button 
              className={`action-btn ${clientData.suspend === 1 ? 'unsuspend-btn' : 'suspend-btn'}`}
              onClick={handleSuspendUnsuspend}
              disabled={suspendLoading || !clientData.email}
            >
              {suspendLoading ? (
                <>
                  <FaSpinner className="fa-spin" />
                  Processing...
                </>
              ) : clientData.suspend === 1 ? (
                <>
                  <FaPlay />
                  Unsuspend Client
                </>
              ) : (
                <>
                  <FaPause />
                  Suspend Client
                </>
              )}
            </button>
            <button 
              className="action-btn trading-btn"
              onClick={() => setShowTradingAccountsView(true)}
            >
              <FaChartLine />
              Trading Accounts
            </button>
            <button className="action-btn history-btn">
              <FaLink />
              Connect Agent Accounts
            </button>
            <button className="action-btn add-account-btn">
              <FaWallet />
              Wallet
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="edit-client-main">
          {!showTradingAccountsView ? (
            <>
              <div className="edit-client-header">
                <h1>{isViewMode ? 'View Client Information' : 'Edit Client Information'}</h1>
                <button className="close-btn" onClick={() => navigate('/clients')}>
                  <FaTimes />
                </button>
              </div>

          <div className="edit-client-form">
            {/* Personal Information */}
            <div className="form-section">
              <div className="section-header">
                <h3>Personal Information</h3>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="firstName"
                      value={clientData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                      className="professional-input"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Last Name</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="lastName"
                      value={clientData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                      className="professional-input"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Date Of Birth</label>
                  <div className="input-wrapper">
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={clientData.dateOfBirth}
                      onChange={handleInputChange}
                      className="professional-input"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Password</label>
                  <div className="input-wrapper">
                    <i className="fas fa-lock input-icon"></i>
                    <input
                      type="text"
                      name="password"
                      value={clientData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      className="professional-input with-icon"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <div className="section-header">
                <h3>Contact Information</h3>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <div className="input-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      name="email"
                      value={clientData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email address"
                      className="professional-input with-icon"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Phone Number</label>
                  <div className="input-wrapper">
                    <i className="fas fa-phone input-icon"></i>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={clientData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                      className="professional-input with-icon"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="form-section">
              <div className="section-header">
                <h3>Location Information</h3>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Zip / Postal Code</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="zipCode"
                      value={clientData.zipCode}
                      onChange={handleInputChange}
                      placeholder="Enter zip code"
                      className="professional-input"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Country</label>
                  <div className="input-wrapper">
                    <i className="fas fa-globe input-icon"></i>
                    <input
                      type="text"
                      name="country"
                      value={clientData.country}
                      onChange={handleInputChange}
                      placeholder="Enter country"
                      className="professional-input with-icon"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>State</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="state"
                      value={clientData.state}
                      onChange={handleInputChange}
                      placeholder="Enter state"
                      className="professional-input"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>City</label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marker-alt input-icon"></i>
                    <input
                      type="text"
                      name="city"
                      value={clientData.city}
                      onChange={handleInputChange}
                      placeholder="Enter city"
                      className="professional-input with-icon"
                      readOnly={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button - Only show in edit mode */}
            {!isViewMode && (
              <div className="form-actions">
                <button 
                  className="save-btn"
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
            </>
          ) : (
            <>
              {/* Trading Accounts View */}
              <div className="trading-accounts-header">
                <h1>Trading Accounts</h1>
                <button className="back-to-edit-btn" onClick={() => setShowTradingAccountsView(false)}>
                  <FaArrowLeft /> Back to Edit
                </button>
              </div>
              
              <div className="trading-accounts-content">
                <div className="trading-accounts-table-container">
                  <div className="table-header">
                    <div className="export-buttons">
                      <button className="export-btn copy-btn">Copy</button>
                      <button className="export-btn csv-btn">CSV</button>
                      <button className="export-btn excel-btn">Excel</button>
                      <button className="export-btn pdf-btn">PDF</button>
                      <button className="export-btn print-btn">Print</button>
                    </div>
                    <div className="column-visibility">
                      <button className="visibility-btn">Column visibility</button>
                    </div>
                    <div className="search-container">
                      <label>Search:</label>
                      <input type="text" className="search-input" />
                    </div>
                  </div>
                  
                  <div className="trading-accounts-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Trade ID</th>
                          <th>Server</th>
                          <th>Account Name</th>
                          <th>Leverage</th>
                          <th>Currency</th>
                          <th>Balance</th>
                          <th>Status</th>
                          <th>Platform</th>
                          <th>Registered Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tradingAccounts.map((account) => (
                          <tr key={account.trade_id}>
                            <td>
                              <div className="account-cell">
                                <i className="fas fa-info-circle info-icon"></i>
                                {account.trade_id}
                              </div>
                            </td>
                            <td>{'PrimeWave Fx Ltd'}</td>
                            <td>{account.account_name}</td>
                            <td>{account.leverage}</td>
                            <td>{account.currency}</td>
                            <td>${parseFloat(account.Balance || 0).toFixed(2)}</td>
                            <td>
                              <span className={`status-badge ${account.status === 'active' ? 'status-verified' : 'status-pending'}`}>
                                {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                              </span>
                            </td>
                            <td>{account.tradePlatform}</td>
                            <td>{new Date(account.Registered_Date).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="table-footer">
                    <div className="entries-info">
                      Showing 1 to 1 of 1 entries
                    </div>
                    <div className="pagination">
                      <button className="page-btn" disabled>Previous</button>
                      <button className="page-btn active">1</button>
                      <button className="page-btn" disabled>Next</button>
                    </div>
                  </div>
                </div>
                
                <div className="agent-account-section">
                  <h3>Agent Account</h3>
                  <div className="agent-account-content">
                    <div className="account-number-section">
                      <label>Account Number</label>
                      <input type="text" value={agentAccount} readOnly className="account-number-input" />
                    </div>
                    <div className="password-section">
                      <label>Change Password</label>
                      <div className="password-controls">
                        <input type="password" className="password-input" />
                        <button className="generate-btn" onClick={handleGeneratePassword}>
                          Generate
                        </button>
                      </div>
                    </div>
                  </div>
                  <button className="create-password-btn">
                    Create Password
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>


    </div>
  );
};

export default EditClient;